import React, { useEffect, useState } from "react"
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, X, Bell, Crown, ChevronDown, Download, ShoppingCart, MessageCircle, Check, Eye, Play } from 'lucide-react';
import SEO from '../components/SEO';
import BreadcrumbSchema from "../components/BreadcrumbSchema";

import ThemeSidebar from '../components/sidebars/ThemeSidebar';
import AdSlot from '../components/AdSlot';
import { Button } from '../components/ui/button';
import themesData from '../data/themes.json';
import Site from '../data/siteConfig.json';

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table"


import Autoplay from "embla-carousel-autoplay"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel"

const ThemeDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const theme = themesData.find((t: any) => t.slug === slug);

  const [api, setApi] = useState<any>(null)
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const buildWhatsappLink = (
    number: string,
    themeName: string,
    plan: string,
    price: string
  ) => {
    const message = `
Hi, I want to buy:

Theme: ${themeName}
Plan: ${plan}
Price: $${price}
License: Lifetime

Please share payment details.
  `.trim()

    return `https://wa.me/${number}?text=${encodeURIComponent(message)}`
  }

  const renderValue = (value: boolean | string) => {
    if (value === true)
      return <Check className="w-5 h-5 text-green-600 ml-auto" />
    if (value === false)
      return <span className="text-gray-400">—</span>
    return <span className="text-gray-900 font-medium">{value}</span>
  }


  if (!theme) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Theme Not Found</h1>
        <Link to="/templates">
          <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
            Back to Templates
          </Button>
        </Link>
      </div>
    );
  }

  const themeData = theme as any;
const FALLBACK_THUMBNAIL =
  `https://placehold.co/600x400/dee3f8/303b5c?text=${encodeURIComponent(
    theme.title
  )}`;

  const relatedThemes = themesData
  .filter((t: any) => {
    if (t.slug === themeData.slug) return false;

    return t.tags?.some((tag: string) =>
      themeData.tags?.includes(tag)
    );
  })
  .slice(0, 4); // max 4 themes

const latestThemes = themesData
  .filter((t: any) => t.slug !== themeData.slug)
  .sort(
    (a: any, b: any) =>
      new Date(b.createdAt).getTime() -
      new Date(a.createdAt).getTime()
  )
  .slice(0, 4);

  const themesToShow =
  relatedThemes.length > 2
    ? relatedThemes.slice(0, 4)
    : latestThemes;

const sectionTitle =
  relatedThemes.length > 2
    ? "Related Themes"
    : "Latest Themes";



  return (
    <>
      <SEO
        title={theme.title}
        description={theme.description}
        image={theme.thumbnail}
        keywords={theme.keywords}
        schemaType="product"
        price={theme.price}
      />
<BreadcrumbSchema
  items={[
    { name: "Home", url: "https://www.thecodebazaar.com/" },
    { name: "Templates", url: "https://www.thecodebazaar.com/templates" },
    {
      name: themeData.title,
      url: `https://www.thecodebazaar.com/theme/${themeData.slug}`,
    },
  ]}
/>

      {/* Header */}
      <section className="bg-gradient-to-br from-blue-100 via-white to-sky-100 to-transparent py-16">
        <div className="container mx-auto px-4">
          <div>
            <Link to="/templates" className="text-blue-600 font-medium hover:underline mb-4 inline-block">
              ← Back to Templates
            </Link>
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              <div className="flex-1">
                <span className="inline-block px-4 py-1 bg-blue-100 text-blue-600 text-sm rounded-full font-medium capitalize mb-4">{theme.category}</span>
                <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                  {theme.title}
                </h1>
                <p className="text-lg text-muted-foreground mb-4">
                  {theme.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {themeData.tags?.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>



              </div>

              <div className="flex sm:grid items-center gap-4 text-center my-auto">
                <div className="text-4xl font-bold text-blue-500 mb-2">${themeData.price}</div>
                <span className="text-base px-4 py-2 border rounded-lg">
                  One-time payment
                </span>
              </div>
            </div>
            {/* <a href="#content" className="block mt-8">
  <ChevronDown className="flex size-12 animate-bounce items-center justify-center rounded-full bg-white p-2 ring-1 ring-gray-900/5 mx-auto"/>
</a> */}


          </div>
        </div>
      </section>


      <div className="container mx-auto px-4 py-12 scroll-mt" id="content">

        {/* Ad Slot */}

        <AdSlot slot="heroBanner"  className="my-2"/>

        <div className="flex flex-col md:grid lg:grid-cols-4 gap-8">

          {/* Main Content */}
          <div className="lg:col-span-3">


            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-8">


              <img
                src={theme.thumbnail || FALLBACK_THUMBNAIL}
                alt={theme.title}
                className="rounded-t-2xl w-full h-full aspect-[16/9] object-cover object-top group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
                onError={(e) => {
    e.currentTarget.src = FALLBACK_THUMBNAIL;
  }}
              />

              <div className="p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                  <div className="mb-4 md:mb-0">
                    <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3">{themeData.title}</h2>

                  </div>

                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4">
                  {themeData.demoUrl && (
                    <Link to={`/theme-preview/${themeData.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 md:flex-none"
                    >
                      <Button variant="outline" className="w-full border-2 border-sky-500 text-sky-500 hover:bg-sky-50">
                        <Eye className="mr-2 w-4 h-4" />
                        Live Preview
                      </Button>
                    </Link>
                  )}
                  <a
                      href="#premium"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                        <ShoppingCart className="mr-2 w-4 h-4" />
                        Buy Now - ${themeData.price}
                      </Button>
                    </a>
                    
                  {(
                    <a
                      href={`https://wa.me/${Site.contact.whatsapp}?text=Hi, I'm interested in ${themeData.title}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 md:flex-none"
                    >
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                        <MessageCircle className="mr-2 w-4 h-4" />
                        Buy via WhatsApp
                      </Button>
                    </a>
                  )}
                </div>

                {themeData.freeVersion && (
                  <>
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-green-700 font-medium">Free version available!</span>
                        <a
                          href="#pricing"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
                            <Download className="mr-2 w-4 h-4" />
                            Download Free
                          </Button>
                        </a>
                      </div>
                    </div>
                    
                  </>
                )}

                <div className="mt-4 p-4 bg-sky-50 border border-sky-200 rounded-lg font-medium">
                      <span className="text-sm text-gray-600">Need Custom Changes in This Template? - <a
                        href="#customise" className="text-sky-600 font-mono font-semibold"
                      >Request Customisation</a></span>
                      {/* <code className="text-sky-600 font-mono font-semibold">{themeData.password}</code> */}
                    </div>

              </div>
            </section>
            {/* Screenshots */}
            {themeData.screenshots?.length > 0 && (
              <div className="mb-8 px-2">
                <h2 className="text-lg sm:text-2xl font-bold mb-6">Screenshots</h2>

                <Carousel
                  setApi={setApi}
                  opts={{ loop: true }}
                  plugins={[
                    Autoplay({
                      delay: 3000,
                      stopOnInteraction: false,
                      stopOnMouseEnter: true,
                    }),
                  ]}
                >
                  <CarouselContent>
                    {themeData.screenshots.map(
                      (img: string, index: number) => (
                        <CarouselItem key={index}>
                          <img
                            src={img}
                            alt={`Screenshot ${index + 1}`}
                            className="aspect-[12/8] w-full rounded-xl shadow-lg"
                            loading="lazy"
                          />
                        </CarouselItem>
                      )
                    )}
                  </CarouselContent>

                  <CarouselPrevious className="bg-white/20 backdrop-blur -left-4" />
                  <CarouselNext className="bg-white/20 backdrop-blur -right-4" />

                  {/* DOTS */}
                  <div className="mt-6 flex justify-center gap-2">
                    {Array.from({ length: count }).map((_, i) => (
                      <div
                        key={i}
                        onClick={() => api?.scrollTo(i)}
                        className={`h-2.5 w-2.5 rounded-full transition ${i === current
                          ? "bg-sky-500 scale-110"
                          : "bg-gray-300 hover:bg-gray-400"
                          }`}
                      />
                    ))}
                  </div>
                </Carousel>
              </div>
            )}

            {/* Features */}
            {themeData.features && themeData.features.length > 0 && (
              <div className="bg-white rounded-2xl p-5 md:p-7 border border-gray-200 shadow-sm mb-8">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-6">Features</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {themeData.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-br from-sky-100 to-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-sky-500" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

{themeData.freeVersion && (
            <AdSlot slot="postInline" className="mb-4" />
)}
            


            {/* ================= PRICING ================= */}
            <div className="mb-10 scroll-mt" id="pricing">
              <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8">
                Choose Your Plan
              </h2>

              <div className="flex flex-col md:grid md:grid-cols-3 gap-6">
{!themeData.freeVersion && (
<AdSlot slot="postInline2" />
)}
                {/* FREE */}
                {themeData.freeVersion && (
                  <div className="border rounded-xl p-6 text-center">
                    <h3 className="text-xl font-semibold mb-2">Free</h3>
                    <p className="text-gray-500 mb-4">For learning & testing</p>

                    <div className="text-4xl font-bold mb-6">$0</div>

                    <ul className="space-y-3 text-sm text-left mb-6">
                      <li className="flex gap-2"><Check className="text-green-500" /> Limited sections</li>
                      <li className="flex gap-2"><Check className="text-green-500" /> Footer credit required</li>
                      <li className="flex gap-2"><X className="text-red-500" /> No updates</li>
                    </ul>


                    <a
                      href={themeData.freeVersion}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" className="border-indigo-600 text-indigo-600 w-full">
                        Download Free
                      </Button>
                    </a>

                    <div className="p-4">
                      <span className="text-sm text-gray-600">Watch the video to <a
                        href="#watch-video" className="text-sky-600 font-mono font-semibold"
                      >Get password</a></span>
                    </div>

                  </div>
                )}

                {/* PREMIUM */}
                <div className="border-2 border-sky-500 rounded-xl p-6 text-center relative scroll-mt" id="premium">
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-xs px-3 py-1 rounded-full">
                    Most Popular
                  </span>

                  <h3 className="text-xl font-semibold mb-2">Premium</h3>
                  <p className="text-gray-500 mb-4">Best for freelancers</p>

                  <div className="text-4xl font-bold mb-6">
                    ${themeData.price}
                  </div>

                  <ul className="space-y-3 text-sm text-left mb-6">
                    <li className="flex gap-2"><Check className="text-green-500" /> All sections unlocked</li>
                    <li className="flex gap-2"><Check className="text-green-500" /> No footer credit</li>
                    <li className="flex gap-2"><Check className="text-green-500" /> Lifetime updates</li>
                  </ul>

                  <a
                    href={buildWhatsappLink(
                      Site.contact.whatsapp,
                      themeData.title,
                      "Premium",
                      themeData.price
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white">
                      Buy Premium
                    </Button>
                  </a>
                </div>

                {/* EXTRA PREMIUM */}
                <div className="border rounded-xl p-6 text-center bg-gray-900 text-white">
                  <h3 className="text-xl font-semibold mb-2 flex justify-center gap-2">
                    <Crown className="text-yellow-400" /> Extra Premium
                  </h3>

                  <p className="text-gray-400 mb-4">For agencies & businesses</p>

                  <div className="text-4xl font-bold mb-6">
                    ${themeData.extraPrice}
                  </div>

                  <ul className="space-y-3 text-sm text-left mb-6">
                    <li className="flex gap-2"><Check className="text-green-400" /> 5 ready-made pages</li>
                    <li className="flex gap-2"><Check className="text-green-400" /> Corporate use allowed</li>
                    <li className="flex gap-2"><Check className="text-green-400" /> Lifetime license</li>
                  </ul>
                  <a
                    href={buildWhatsappLink(
                      Site.contact.whatsapp,
                      themeData.title,
                      "Extra Premium",
                      themeData.extraPrice
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="animate-pulse w-full bg-yellow-400 text-black hover:bg-yellow-500">
                      Buy Extra Premium
                    </Button>
                  </a>
                </div>

              </div>
            </div>


            {/* ================= COMPARISON TABLE ================= */}

            <div className="border border-gray-200 shadow-lg mb-6">

              <Table className="table-fixed w-full">
                <>

                  {/* <caption className="p-5 text-lg font-medium text-left text-heading border-b mb-4">
            Feature Compare
            <p className="mt-1.5 text-sm font-normal text-body">Browse a list of Flowbite products designed to help you work and play, stay organized, get answers, keep in touch, grow your business, and more.</p>
        </caption> */}
                </>
                <TableHeader className="bg-gray-200">
                  <TableRow>
                    <TableHead className="w-[30%] text-left whitespace-nowrap px-4 py-4 font-medium text-gray-700 font-semibold text-gray-800">Theme Features</TableHead>
                    {themeData.freeVersion && (<TableHead className="text-right px-4 py-4 font-medium text-gray-700 font-semibold text-gray-800">Free</TableHead>)}
                    <TableHead className="text-right px-4 py-4 font-medium text-gray-700 font-semibold text-gray-800">Premium</TableHead>
                    <TableHead className="text-right px-4 py-4 font-medium text-gray-700 font-semibold text-gray-800">Extra Premium</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {[
                    {
                      section: "Core Features",
                      rows: [
                        ["SEO Optimized", true, true, true],
                        ["Layout Feature", false, true, true],
                        ["Advanced Sections", false, true, true],
                        ["Footer Credit Removed", false, true, true],
                        ["Drop Down Menu", false, true, true],
                      ],
                    },
                    {
                      section: "License & Usage",
                      rows: [
                        ["Personal Use", true, true, true],
                        ["Freelance Projects", false, true, true],
                        ["Corporate / Client Use", false, false, true],
                      ],
                    },
                    {
                      section: "Extras",
                      rows: [
                        ["Lifetime Updates", false, true, true],
                        ["5 Ready-made Pages", false, false, true],
                        ["One-time Payment", true, true, true],
                        ["Lifetime License", false, true, true],
                      ],
                    },
                  ].map((group, i) => (
                    <React.Fragment key={i}>
                      <TableRow className="bg-gray-50">
                        {themeData.freeVersion && (
                        <TableCell colSpan={4} className="px-4 py-4 font-medium text-gray-700 font-semibold text-gray-800">
                          {group.section}
                        </TableCell>
                        )}
                        {!themeData.freeVersion && (
                        <TableCell colSpan={3} className="px-4 py-4 font-medium text-gray-700 font-semibold text-gray-800">
                          {group.section}
                        </TableCell>
                        )}
                      </TableRow>

                      {group.rows.map(([label, free, premium, extra], index) => (
                        <TableRow key={index} className="bg-neutral-primary border-b border-default">
                          <TableCell className="w-[40%] text-left px-4 py-4">
                            {label}
                          </TableCell>
                          {themeData.freeVersion && (<TableCell className="w-[10%] text-right px-4 py-4">
                            {renderValue(free)}
                          </TableCell>)}
                          <TableCell className="w-[20%] text-right px-4 py-4">
                            {renderValue(premium)}
                          </TableCell>
                          <TableCell className="w-[20%] text-right px-4 py-4">
                            {renderValue(extra)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>


{/* ================= Customization CTA ================= */}
<section className="mt-10 mb-4 scroll-mt" id="customise">
  <div className="relative overflow-hidden rounded-3xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-blue-50 p-7 md:p-10">
    
    {/* Glow */}
    <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-sky-200/30 blur-3xl" />
    
    <div className="relative grid lg:grid-cols-2 gap-10 items-center">
      
      {/* Left Content */}
      <div>
        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Need Custom Changes in This Template?
        </h3>

        <p className="text-gray-600 text-lg mb-6 leading-relaxed">
          Want this theme tailored exactly to your needs?  
          We provide professional customization services to match your brand,
          content, and business goals.
        </p>

        <ul className="space-y-3 text-gray-700">
          <li>✔ Layout & section customization</li>
          <li>✔ Color, font & branding changes</li>
          <li>✔ Additional pages & features</li>
          <li>✔ SEO & performance optimization</li>
          <li>✔ Client & corporate use ready</li>
        </ul>

        <p className="mt-6 text-sm text-gray-500">
          One-time customization • Lifetime usage • Expert support
        </p>
      </div>

      {/* Right CTA Card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 md:p-8">
        <h4 className="text-xl font-semibold text-gray-900 mb-3">
          Get a Custom Quote
        </h4>

        <p className="text-gray-600 mb-6 text-sm">
          Share your requirements and we’ll suggest the best customization plan
          with pricing and timeline.
        </p>

        <div className="flex flex-col gap-4">
          {/* WhatsApp CTA */}
          <a
            href={`https://api.whatsapp.com/send/?phone=+91${themeData.whatsappNumber}&text=${encodeURIComponent(
              `Hi, I want to customize the "${themeData.themeName}" template.`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full text-center rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 text-white font-semibold hover:shadow-lg transition-all"
          >
            Chat on WhatsApp
          </a>

          {/* Hire Us CTA */}
          <a
            href="/hire"
            className="w-full text-center rounded-xl border border-gray-300 px-6 py-4 font-semibold text-gray-900 hover:bg-gray-50 transition"
          >
            Request Custom Work
          </a>
        </div>
      </div>
    </div>
  </div>
</section>


<AdSlot slot="postInline2"  className="my-2"/>

            {/* Custom HTML Content */}
            {themeData.customHtml && (
              // <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm mb-8">
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: themeData.customHtml }}
              />
              // </div>
            )}


            {/* Ad Slot */}
            <AdSlot slot="inArticle1"  className="my-2"/>
            

{!themeData.videoUrl && (
      <div className="bg-gradient-to-br from-red-50 via-white to-pink-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="w-40 h-40 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 p-1">

            <a href={Site.youtube.channel}
            >
              <img src="/assets/wd.png" className="aspect-[5/5] bg-white w-full rounded-full" alt={Site.youtube.name} loading="lazy" />
            </a>
          </div>
          <h1 className="text-3xl md:text-6xl font-bold text-gray-900 mb-6">
            {Site.youtube.name} YouTube
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {Site.youtube.description}
          </p>
          <a
            href={Site.youtube.channel}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              <Bell className="mr-2 w-5 h-5" />
              Subscribe Now
            </Button>
          </a>
        </div>
      </div>
      )}

            {/* Setup Video */}
            {themeData.videoUrl && (
              <div className="border-t mt-6 pt-4 mb-8 scroll-mt" id="watch-video">
                <h3 className="text-lg sm:text-2xl font-bold mb-4">
                  {themeData.themeName} Setup & Customisation Tutorial
                </h3>

                <p className="text-gray-600 mb-2 max-w-2xl">
                  Watch the complete walkthrough of the {themeData.themeName} to understand
                  its design, features, and customization options.
                </p>
                {themeData.freeVersion && (
                <div className="text-gray-600 mb-6 max-w-2xl">
                  Watch the video to <a
                    href="#watch-video" className="text-sky-600 font-mono font-semibold"
                  >Get password</a>

                </div>
                )}

                <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-lg hover:shadow-sm transition-all duration-300 group cursor-pointer"
                >
                  <div className="aspect-video rounded-top-xl overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${themeData.videoUrl}`}
                      title="Setup Video"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>

                  <div className="p-4">
                    <a
                      href={`https://www.youtube.com/watch?v=${themeData.videoUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <h3 className="text-lg sm:text-xl md:text-2xl flex gap-2 items-center font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors border-b pb-2">
                        {themeData.title} <ExternalLink />
                      </h3>
                    </a>
                    <div className="mt-4 flex items-center space-x-1 justify-between">
                      <a className="flex items-center gap-2 text-sm sm:text-lg md:text-2xl"
                        href={Site.youtube.channel}
                      ><img src="/assets/wd.png" className="border-2 border-red-600 w-10 rounded-full aspect-[2/2]" alt={Site.youtube.name} loading="lazy" /><span className="text-sky-600 font-semibold">{Site.youtube.name}</span></a>

                      <a
                        href={Site.youtube.channel}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="text-sm sm:text-lg bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md shadow-lg hover:shadow-xl transition-all duration-300">
                          <Bell className="w-4 h-5" />
                          Subscribe Now
                        </Button>
                      </a>
                    </div>
                    {/* <span>Website Developer's</span> */}
                  </div>
                </div>
              </div>
            )}

{themesToShow.length > 0 && (
  <div className="bg-white rounded-2xl p-5 md:p-7 border border-gray-200 shadow-sm mb-8">
    <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-6">
      {sectionTitle}
    </h2>

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {themesToShow.map((theme: any) => (
        <Link
          key={theme.id}
          to={`/theme/${theme.slug}`}
          className="group bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300"
        >
          {/* Image */}
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={
                theme.thumbnail ||
                `https://placehold.co/600x400/dee3f8/303b5c?text=${encodeURIComponent(
                  theme.title
                )}`
              }
              alt={theme.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2">
              {theme.title}
            </h3>

            <div className="flex flex-wrap gap-1 mb-3">
              {theme.tags?.slice(0, 3).map((tag: string) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-0.5 bg-sky-50 text-sky-600 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span className="font-bold text-sky-600">
                ${theme.price}
              </span>
              <span className="text-xs text-gray-500">
                View →
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>

    <AdSlot slot="multiplex1" className="my-2"/>
  </div>
)}


          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <ThemeSidebar />
          </div>

        </div>
      </div>
      {themeData.videoUrl && (
      <div className="bg-gradient-to-br from-red-50 via-white to-pink-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="w-40 h-40 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 p-1">

            <a href={Site.youtube.channel}
            >
              <img src="/assets/wd.png" className="aspect-[5/5] bg-white w-full rounded-full" alt={Site.youtube.name} loading="lazy" />
            </a>
          </div>
          <h1 className="text-3xl md:text-6xl font-bold text-gray-900 mb-6">
            {Site.youtube.name} YouTube
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {Site.youtube.description}
          </p>
          <a
            href={Site.youtube.channel}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
              <Bell className="mr-2 w-5 h-5" />
              Subscribe Now
            </Button>
          </a>
        </div>
      </div>
      )}
      {/* <AdSlot slot="footer" /> */}

    </>
  );
};

export default ThemeDetail;